'use client';

import { useMemo, useRef, useState, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { SField, type SFieldAddonAlign, type SFieldSize, type SFieldStatus } from '../SField';
import { SIcon, type SIconName } from '../SIcon';
import { SGhostButton } from '../SGhostButton';
import { type SColor } from '../../lib/color';
import { cn } from '../../lib/cn';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { type STooltipProps } from '../STooltip';
import {
  TIME_RANGE_PICKER_SIZE_CONFIG,
  clampTimePart,
  formatTimeRangeDisplay,
  formatTimeValue,
  normalizeTimeRangeValue,
  parseTimeValue,
  type STimeRangePickerMeridiem,
  type STimeRangePickerRangeOrder,
  type STimeRangePickerSize,
  type STimeRangePickerType,
  type STimeRangeValue,
} from './time-range-picker.config';

export type {
  STimeRangePickerSize,
  STimeRangePickerRangeOrder,
  STimeRangePickerType,
  STimeRangeValue,
} from './time-range-picker.config';

const TIME_RANGE_SELECTOR_WIDTH = 345;
const MIDDAY_FIELD_MIN_WIDTH: Record<STimeRangePickerSize, number> = {
  sm: 178,
  md: 216,
};

export interface STimeRangePickerProps {
  /** 선택 시간 범위 [시작, 종료] (HH:mm) */
  value?: STimeRangeValue;
  /** 선택 변경 (sdUpdate) */
  onValueChange?: (range: STimeRangeValue) => void;
  /** 열림/닫힘 변경 (sdDropDownShow) */
  onOpenChange?: (open: boolean) => void;
  /** 표시 타입: default(24시간) / midday(오전·오후) */
  type?: STimeRangePickerType;
  size?: STimeRangePickerSize;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** 오전/오후 선택 표시 여부. 지정하지 않으면 type="midday"일 때만 켜집니다. */
  useMeridiem?: boolean;
  minuteStep?: number;
  /** 범위 순서 정책. strict는 시작 시간이 종료 시간보다 늦어지지 않도록 보정합니다. */
  rangeOrder?: STimeRangePickerRangeOrder;
  width?: number | string;
  name?: string;
  rules?: Rule[];
  status?: SFieldStatus;
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  style?: CSSProperties;
}

type RangeSide = 'start' | 'end';
type RangePart = 'hour' | 'minute';
type EditingKey = `${RangeSide}-${RangePart}`;
type ChangeDirection = 'increase' | 'decrease' | 'unknown';

export function RangeTimeSelector({
  value,
  useMeridiem,
  minuteStep,
  rangeOrder,
  onChange,
}: {
  value?: STimeRangeValue;
  useMeridiem: boolean;
  minuteStep: number;
  rangeOrder: STimeRangePickerRangeOrder;
  onChange: (range: [string, string]) => void;
}) {
  const range = useMemo<[string, string]>(() => value ?? ['00:00', '23:59'], [value]);
  const [editingKey, setEditingKey] = useState<EditingKey | null>(null);
  const [inputDraft, setInputDraft] = useState<Partial<Record<EditingKey, string>>>({});
  const autoAdjustedRef = useRef<{
    side: RangeSide;
    direction: Exclude<ChangeDirection, 'unknown'>;
  } | null>(null);

  const getParts = (side: RangeSide) => parseTimeValue(side === 'start' ? range[0] : range[1]);
  const getMeridiem = (side: RangeSide): STimeRangePickerMeridiem =>
    getParts(side).hour >= 12 ? 'PM' : 'AM';
  const getDisplayHour = (side: RangeSide) => {
    const { hour } = getParts(side);
    if (!useMeridiem) return hour;
    const displayHour = hour % 12;
    return displayHour === 0 ? 12 : displayHour;
  };

  const commit = (
    side: RangeSide,
    hour: number,
    minute: number,
    direction: ChangeDirection = 'unknown',
  ) => {
    const next = formatTimeValue(hour, minute);
    onChange(
      normalizeNextRange(side === 'start' ? [next, range[1]] : [range[0], next], side, direction),
    );
  };

  const getTimeMinutes = (time: string) => {
    const { hour, minute } = parseTimeValue(time);
    return hour * 60 + minute;
  };

  const normalizeNextRange = (
    nextRange: [string, string],
    changedSide: RangeSide,
    direction: ChangeDirection,
  ): [string, string] => {
    if (rangeOrder === 'allow-cross-day') return nextRange;
    const [start, end] = nextRange;
    const [currentStart, currentEnd] = range;
    if (getTimeMinutes(start) <= getTimeMinutes(end)) {
      const shouldKeepSynced =
        autoAdjustedRef.current?.side === changedSide &&
        currentStart === currentEnd &&
        start !== end &&
        (direction === 'unknown' || autoAdjustedRef.current.direction === direction);

      if (shouldKeepSynced) {
        return changedSide === 'start' ? [start, start] : [end, end];
      }
      autoAdjustedRef.current = null;
      return nextRange;
    }
    autoAdjustedRef.current = {
      side: changedSide,
      direction: changedSide === 'start' ? 'increase' : 'decrease',
    };
    return changedSide === 'start' ? [start, start] : [end, end];
  };

  const changeHour = (side: RangeSide, delta: number) => {
    const { hour, minute } = getParts(side);
    commit(side, (hour + delta + 24) % 24, minute, delta > 0 ? 'increase' : 'decrease');
  };

  const changeMinute = (side: RangeSide, delta: number) => {
    const { hour, minute } = getParts(side);
    const next = minute + delta;
    if (next > 59) {
      commit(side, (hour + 1) % 24, next - 60, delta > 0 ? 'increase' : 'decrease');
      return;
    }
    if (next < 0) {
      commit(side, (hour + 23) % 24, next + 60, delta > 0 ? 'increase' : 'decrease');
      return;
    }
    commit(side, hour, next, delta > 0 ? 'increase' : 'decrease');
  };

  const setMeridiem = (side: RangeSide, next: STimeRangePickerMeridiem) => {
    const current = getMeridiem(side);
    if (current === next) return;
    const { hour, minute } = getParts(side);
    commit(side, next === 'AM' ? hour - 12 : hour + 12, minute);
  };

  const getEditingKey = (side: RangeSide, part: RangePart): EditingKey => `${side}-${part}`;
  const sanitizeInput = (next: string) => next.replace(/\D/g, '').slice(0, 2);

  const beginEdit = (side: RangeSide, part: RangePart, currentValue: number) => {
    const key = getEditingKey(side, part);
    setEditingKey(key);
    setInputDraft(prev => ({ ...prev, [key]: String(currentValue).padStart(2, '0') }));
  };

  const commitHourDraft = (side: RangeSide, draft: string) => {
    const clamped = clampTimePart(Number(draft), useMeridiem ? 1 : 0, useMeridiem ? 12 : 23);
    const parts = getParts(side);
    const meridiem = getMeridiem(side);
    const nextHour = useMeridiem
      ? meridiem === 'PM'
        ? clamped === 12
          ? 12
          : clamped + 12
        : clamped === 12
          ? 0
          : clamped
      : clamped;
    commit(side, nextHour, parts.minute);
    return clamped;
  };

  const commitMinuteDraft = (side: RangeSide, draft: string) => {
    const { hour } = getParts(side);
    const clamped = clampTimePart(Number(draft), 0, 59);
    commit(side, hour, clamped);
    return clamped;
  };

  const endEdit = (side: RangeSide, part: RangePart) => {
    const key = getEditingKey(side, part);
    const draft = inputDraft[key];
    if (part === 'hour' && draft != null && draft !== '') {
      commitHourDraft(side, draft);
    }
    if (part === 'minute' && draft != null && draft !== '') {
      commitMinuteDraft(side, draft);
    }
    setEditingKey(null);
    setInputDraft({});
  };

  const inputStyle = {
    width: 'var(--cmp-timepicker-selector-time-input-width)',
    height: 'var(--cmp-textinput-sm-height)',
    borderRadius: 'var(--cmp-textinput-sm-radius)',
    border: 'var(--cmp-textinput-borderWidth) solid var(--cmp-textinput-border-default)',
    color: 'var(--cmp-textinput-text-default)',
    fontSize: 12,
    lineHeight: '20px',
  } as CSSProperties;

  const renderColumn = (side: RangeSide, kind: 'hour' | 'minute') => {
    const isHour = kind === 'hour';
    const { minute } = getParts(side);
    const val = isHour ? getDisplayHour(side) : minute;
    const currentEditingKey = getEditingKey(side, kind);
    const displayValue =
      editingKey === currentEditingKey
        ? (inputDraft[currentEditingKey] ?? '')
        : String(val).padStart(2, '0');

    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ gap: 'var(--cmp-timepicker-selector-time-column-gap)' }}
      >
        <SGhostButton
          icon="chevronUp"
          size="xs"
          ariaLabel={`${side === 'start' ? '시작' : '종료'} ${isHour ? '시' : '분'} 증가`}
          onClick={() => (isHour ? changeHour(side, 1) : changeMinute(side, minuteStep))}
        />
        <input
          aria-label={`${side === 'start' ? '시작' : '종료'} ${isHour ? '시' : '분'}`}
          inputMode="numeric"
          value={displayValue}
          placeholder="-"
          onFocus={() => beginEdit(side, kind, val)}
          onBlur={() => endEdit(side, kind)}
          onChange={event => {
            const draft = sanitizeInput(event.target.value);
            setInputDraft(prev => ({ ...prev, [currentEditingKey]: draft }));
            if (draft === '') return;

            const raw = Number(draft);
            if (isHour) {
              if (draft.length < 2) return;
              const clamped = commitHourDraft(side, draft);
              const displayDraft = raw === clamped ? draft : String(clamped);
              setInputDraft(prev => ({ ...prev, [currentEditingKey]: displayDraft }));
              return;
            }
            if (draft.length < 2) return;
            const clamped = commitMinuteDraft(side, draft);
            const displayDraft = raw === clamped ? draft : String(clamped);
            setInputDraft(prev => ({ ...prev, [currentEditingKey]: displayDraft }));
          }}
          className="box-border bg-white text-center outline-none"
          style={inputStyle}
        />
        <SGhostButton
          icon="chevronDown"
          size="xs"
          ariaLabel={`${side === 'start' ? '시작' : '종료'} ${isHour ? '시' : '분'} 감소`}
          onClick={() => (isHour ? changeHour(side, -1) : changeMinute(side, -minuteStep))}
        />
      </div>
    );
  };

  const renderPanel = (side: RangeSide, title: string) => (
    <div
      className="flex flex-col items-center justify-center"
      style={{ gap: 'var(--cmp-timepicker-selector-range-section-gap)' }}
    >
      <div
        className="flex flex-col items-center"
        style={{ gap: 'var(--cmp-timepicker-selector-range-heading-gap)' }}
      >
        <div className="whitespace-nowrap text-center text-[12px] font-bold leading-[20px] text-[var(--cmp-timepicker-selector-range-heading-color)]">
          {title}
        </div>
        {useMeridiem && (
          <div className="isolate flex">
            {(['AM', 'PM'] as const).map((item, index) => {
              const selected = item === getMeridiem(side);
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setMeridiem(side, item)}
                  className={cn(
                    'box-border h-[var(--cmp-textinput-sm-height)] w-[61px] cursor-pointer border border-solid bg-white text-center text-[12px] font-medium leading-[20px]',
                    index === 0
                      ? 'rounded-l-[var(--cmp-select-sm-radius)] -mr-px'
                      : 'rounded-r-[var(--cmp-select-sm-radius)]',
                    selected
                      ? 'z-[1] border-[var(--cmp-timepicker-border-focus)] text-[var(--cmp-timepicker-border-focus)]'
                      : 'border-[var(--cmp-timepicker-border-default)] text-[var(--cmp-field-hint-color)]',
                  )}
                >
                  {item === 'AM' ? '오전' : '오후'}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div
        className="flex items-center justify-center"
        style={{
          gap: 'var(--cmp-timepicker-selector-time-gap)',
          padding: '0 var(--cmp-timepicker-selector-section-paddingX)',
        }}
      >
        {renderColumn(side, 'hour')}
        <span
          className="text-center"
          style={{
            width: 'var(--cmp-timepicker-selector-colon-size)',
            color: 'var(--cmp-field-hint-color)',
            fontSize: 'var(--cmp-timepicker-selector-colon-size)',
            lineHeight: 'var(--cmp-timepicker-selector-colon-size)',
          }}
        >
          :
        </span>
        {renderColumn(side, 'minute')}
      </div>
    </div>
  );

  return (
    <div
      className="flex items-start justify-center overflow-hidden"
      style={{
        gap: 'var(--cmp-timepicker-selector-range-panelGap)',
        padding: 'var(--cmp-timepicker-selector-range-paddingXY)',
        background: 'var(--cmp-timepicker-selector-bg)',
        borderRadius: 'var(--cmp-timepicker-selector-radius)',
        boxShadow: 'var(--shadow-spread-sm), var(--shadow-spread-md)',
      }}
    >
      {renderPanel('start', '시작 시간')}
      <div className="self-stretch w-px bg-[var(--sys-color-divider-default)]" />
      {renderPanel('end', '종료 시간')}
    </div>
  );
}

/** STimeRangePicker — sd-time-range-picker 포팅. SField + Popover(RangeTimeSelector). */
export function STimeRangePicker({
  value,
  onValueChange,
  onOpenChange,
  type = 'default',
  size = 'sm',
  placeholder = '00:00 ~ 23:59',
  disabled = false,
  clearable = false,
  useMeridiem,
  minuteStep = 1,
  rangeOrder = 'strict',
  width,
  name,
  rules,
  status,
  label,
  labelWidth,
  icon,
  iconColor,
  labelTooltip,
  labelTooltipProps,
  addonLabel,
  addonAlign,
  hint,
  error,
  errorMessage,
  className,
  style,
}: STimeRangePickerProps) {
  const portalContainer = usePortalContainer();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [popoverAlignOffset, setPopoverAlignOffset] = useState(0);
  const [focused, setFocused] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const dims = TIME_RANGE_PICKER_SIZE_CONFIG[size] ?? TIME_RANGE_PICKER_SIZE_CONFIG.sm;
  const normalized = normalizeTimeRangeValue(value ?? null, rangeOrder);
  const effectiveUseMeridiem = useMeridiem ?? type === 'midday';
  const displayText = formatTimeRangeDisplay(
    normalized,
    effectiveUseMeridiem ? 'midday' : 'default',
  );
  const hasValue = normalized != null && normalized[0] !== '' && normalized[1] !== '';

  useFormField({
    name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);

  const commitOpen = (next: boolean) => {
    if (disabled) return;
    if (next) {
      const triggerWidth = triggerRef.current?.getBoundingClientRect().width ?? 0;
      setPopoverAlignOffset(Math.max(0, (triggerWidth - TIME_RANGE_SELECTOR_WIDTH) / 2));
    }
    setOpen(next);
    onOpenChange?.(next);
    if (!next && rules && rules.length > 0) setRuleError(runRules(value, rules));
  };

  const commitValue = (next: STimeRangeValue) => {
    onValueChange?.(next);
    if (rules && rules.length > 0) setRuleError(runRules(next, rules));
  };

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      size={size as SFieldSize}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused || open}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      width={width}
      minWidth={effectiveUseMeridiem ? MIDDAY_FIELD_MIN_WIDTH[size] : dims.fieldMinWidth}
      disabled={disabled}
      className={className}
      style={style}
    >
      <RPopover.Root open={open} onOpenChange={commitOpen}>
        <RPopover.Trigger asChild>
          <div
            ref={triggerRef}
            role="button"
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            data-name={name}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={event => {
              if (disabled) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                commitOpen(!open);
              }
            }}
            className={cn(
              'flex h-full w-full items-center overflow-hidden bg-transparent outline-none',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            style={{ padding: `0 ${dims.paddingX}`, gap: dims.gap, borderRadius: dims.radius }}
          >
            <SIcon
              name="clockOutline"
              size={dims.icon}
              color={
                disabled
                  ? 'var(--cmp-timepicker-icon-disabled)'
                  : 'var(--cmp-timepicker-icon-default)'
              }
              className="flex-shrink-0"
            />
            <span
              className="flex-1 truncate text-center"
              style={{
                minWidth: dims.minWidth,
                fontSize: dims.fontSize,
                lineHeight: `${dims.lineHeight}px`,
                color: disabled
                  ? 'var(--cmp-timepicker-text-disabled)'
                  : hasValue
                    ? 'var(--cmp-timepicker-text-default)'
                    : 'var(--cmp-field-hint-color)',
              }}
            >
              {hasValue ? displayText : placeholder}
            </span>
            {clearable && hasValue && !disabled && (
              <SGhostButton
                icon="close"
                size="xs"
                ariaLabel="시간 범위 지우기"
                onClick={event => {
                  event.stopPropagation();
                  commitValue(null);
                }}
              />
            )}
          </div>
        </RPopover.Trigger>
        <RPopover.Portal container={portalContainer}>
          <RPopover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            alignOffset={popoverAlignOffset}
            className={cn('z-50', FLOATING_SLIDE_ANIM)}
          >
            <RangeTimeSelector
              value={normalized}
              useMeridiem={effectiveUseMeridiem}
              minuteStep={minuteStep}
              rangeOrder={rangeOrder}
              onChange={commitValue}
            />
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </SField>
  );
}
